import React, { useState, useEffect } from 'react';
import { Icon } from '@chakra-ui/react';
import { LuEye, LuMailbox, LuLock, LuEyeOff, LuMessageSquare, LuPhone, LuBell, LuSmartphone, LuMail } from 'react-icons/lu';
import * as Yup from 'yup';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWrapper } from '../../helpers';
import './Login.css'; // Your existing CSS file
import logo from "../../images/Logo.png"
import { CiLocationArrow1 } from "react-icons/ci";
const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const FormSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  });

  
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Login submission function
  const onSubmit = async () => {
    try {
      setIsLoading(true);
      setErrors({});

      // Validate form data
      await FormSchema.validate(formData, { abortEarly: false });

      let data = formData;

      const url = `${process.env.REACT_APP_API_URL}/user/login`;
      const loginData = await fetchWrapper.post(url, data);

      if (loginData?.accessToken) {
        const token = loginData.accessToken
        localStorage.setItem("user", token);
        localStorage.setItem("customer_id", loginData?.id);
        navigate("/dashboard");

      } else {
        toast.error("Invalid email or password")
      }
    }
    catch (err) {
      if (err.name === 'ValidationError') {
        const validationErrors = {};
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      } else {
        toast(`Login failed: ${err.message || err}`, {
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="login-container">
       
      {/* Left Side - Form */}
      <div className="login-left-side left-side-responsive" >
    
        <div className="login-form-container" >
          {/* Logo */}
          <div className="login-logo-section">
            <img src={logo} alt="Company Logo" />
            <h2 className="login-welcome-title">Welcome back</h2>
            <p className="login-welcome-subtitle">Please login into your account</p>
            <div class="basic-chip outline-green">Admin Portal</div>
          </div>

          {/* Form */}
          <form className="login-form-section" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="login-field-container">
              <label htmlFor="email" className="login-label">
                Email address
              </label>
              <div className="login-input-container">
                <div className="login-input-icon">
                  <LuMailbox />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className={`login-input ${errors.email ? 'login-input-error' : ''}`}
                  placeholder="Enter your Email"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="login-error-text">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="login-field-container">
              <label htmlFor="password" className="login-label">
                Password
              </label>
              <div className="login-input-container">
                <div className="login-input-icon">
                  <Icon as={LuLock} />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className={`login-input login-password-input ${errors.password ? 'login-input-error' : ''}`}
                  placeholder="Enter your Password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="login-eye-button"
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <LuEyeOff /> : <LuEye />}
                </button>
              </div>
              {errors.password && (
                <p className="login-error-text">{errors.password}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="login-submit-button"
              aria-label="Login to your account"
            >
              {isLoading ? (
                <div className="login-spinner"></div>
              ) : (
                <>
                 Sign In
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Right Side - Multi-Channel Notifications */}
      <div className="login-right-side right-side-responsive">
        <div className="login-right-content">
          {/* Header */}
          <div className="login-feature-header">
            <h3 className="login-feature-title">Multi-Channel Notifications</h3>
            <p className="login-feature-subtitle">
              Reach your customers across multiple communication channels with intelligent routing and comprehensive analytics.
            </p>
          </div>

          {/* Channels Grid */}
          <div className="login-channels-grid">
            {/* WhatsApp */}
            <div className="login-channel-card">
              <div className="login-channel-icon login-channel-icon-whatsapp">
                <LuMessageSquare />
              </div>
              <h4 className="login-channel-name">WhatsApp</h4>
              <p className="login-channel-description">Rich messaging with media support</p>
            </div>

            {/* SMS */}
            <div className="login-channel-card">
              <div className="login-channel-icon login-channel-icon-sms">
                <CiLocationArrow1  />
              </div>
              <h4 className="login-channel-name">SMS</h4>
              <p className="login-channel-description">Universal text messaging</p>
            </div>

            {/* Voice */}
            <div className="login-channel-card">
              <div className="login-channel-icon login-channel-icon-voice">
                <LuPhone/>
              </div>
              <h4 className="login-channel-name">Voice</h4>
              <p className="login-channel-description">Automated voice calls</p>
            </div>

            {/* Push */}
            <div className="login-channel-card">
              <div className="login-channel-icon login-channel-icon-push">
                <LuBell/>
              </div>
              <h4 className="login-channel-name">Push</h4>
              <p className="login-channel-description">Mobile push notifications</p>
            </div>

            {/* In-App */}
            <div className="login-channel-card">
              <div className="login-channel-icon login-channel-icon-inapp">
              <LuSmartphone/>
              </div>
              <h4 className="login-channel-name">In-App</h4>
              <p className="login-channel-description">In-app notifications</p>
            </div>

            {/* Email */}
            <div className="login-channel-card">
              <div className="login-channel-icon login-channel-icon-email">
              <LuMail/>
              </div>
              <h4 className="login-channel-name">Email</h4>
              <p className="login-channel-description">Rich email notifications</p>
            </div>
          </div>

          {/* Features */}
          <div className="login-features-footer">
            <div className="login-feature-item">
              <div className="login-feature-dot login-feature-dot-green"></div>
              <span>Real-time Analytics</span>
            </div>
            <div className="login-feature-item">
              <div className="login-feature-dot login-feature-dot-blue"></div>
              <span>Smart Routing</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;